// frontend/src/pages/PainelParceiroPage/PainelParceiroPage.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, Row, Col, Typography, Button, Table, Tag, message, Spin, Alert, Space, QRCode, Statistic, Divider,
} from 'antd';
import {
  CopyOutlined, DownloadOutlined, TeamOutlined, WalletOutlined, LinkOutlined, BarcodeOutlined,
} from '@ant-design/icons';
import { getPainelAfiliado, getIndicacoesAfiliado } from '../../services/authService';
import { getErrorMessage } from '../../utils/errorHandler';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

const { Title, Text } = Typography;

const STATUS_MAP = {
  pendente: { color: 'processing', label: 'Pendente' },
  confirmada: { color: 'success', label: 'Confirmada' },
  paga: { color: 'default', label: 'Paga' },
  cancelada: { color: 'error', label: 'Cancelada' },
};

function PainelParceiroPage() {
  const [painel, setPainel] = useState(null);
  const [indicacoes, setIndicacoes] = useState({ results: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingIndicacoes, setLoadingIndicacoes] = useState(false);
  const [error, setError] = useState('');
  const qrRef = useRef(null);

  const loadPainel = async () => {
    try {
      setError('');
      const data = await getPainelAfiliado();
      setPainel(data);
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao carregar painel.'));
    } finally {
      setLoading(false);
    }
  };

  const loadIndicacoes = async (page = 1, pageSize = 20) => {
    setLoadingIndicacoes(true);
    try {
      const data = await getIndicacoesAfiliado({ page, page_size: pageSize });
      setIndicacoes({
        results: data.results || [],
        count: data.count ?? (data.results?.length ?? 0),
      });
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao carregar indicações.'));
    } finally {
      setLoadingIndicacoes(false);
    }
  };

  useEffect(() => {
    loadPainel();
  }, []);

  useEffect(() => {
    if (painel) loadIndicacoes();
  }, [painel]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard?.writeText(text).then(
      () => message.success(`${label} copiado!`),
      () => message.error('Não foi possível copiar.')
    );
  };

  const formatBRL = (valor) => {
    if (valor == null) return 'R$ 0,00';
    const n = Number(valor);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(n);
  };

  const columns = [
    {
      title: 'Indicado',
      dataIndex: 'usuario_indicado_email',
      key: 'email',
      render: (email, record) => (
        <span>
          {record.usuario_indicado_nome || email || '—'}
          {email && <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{email}</Text>}
        </span>
      ),
    },
    {
      title: 'Cadastro',
      dataIndex: 'data_cadastro',
      key: 'data_cadastro',
      render: (v) => v ? dayjs(v).locale('pt-br').format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Ativação',
      dataIndex: 'data_ativacao',
      key: 'data_ativacao',
      render: (v) => v ? dayjs(v).locale('pt-br').format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const s = STATUS_MAP[status] || { color: 'default', label: status };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'Comissão',
      dataIndex: 'valor_comissao',
      key: 'valor_comissao',
      render: (v) => formatBRL(v),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Spin size="large" tip="Carregando painel..." />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <Title level={4}>Painel do Parceiro</Title>
      <Text type="secondary">
        Seu código, link de divulgação, saldo e histórico de indicações.
      </Text>

      {error && (
        <Alert message={error} type="error" showIcon style={{ marginTop: 16 }} />
      )}

      {painel && (
        <>
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} sm={24} md={12}>
              <Card size="small" title={<><BarcodeOutlined /> Código de afiliado</>}>
                <Space>
                  <Text strong style={{ fontSize: 18 }}>{painel.codigo_afiliado || '—'}</Text>
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(painel.codigo_afiliado, 'Código')}
                  />
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Card size="small" title={<><LinkOutlined /> Link de divulgação</>}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text ellipsis style={{ maxWidth: '100%' }}>{painel.link_divulgacao || '—'}</Text>
                  <Button
                    type="primary"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(painel.link_divulgacao, 'Link')}
                  >
                    Copiar link
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Saldo confirmado"
                  value={painel.saldo_confirmado ?? 0}
                  prefix="R$"
                  precision={2}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Pendente (7 dias)"
                  value={painel.total_pendente ?? 0}
                  prefix="R$"
                  precision={2}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Card size="small">
                <Space>
                  <WalletOutlined />
                  <div>
                    <Text strong>Mínimo R$ 50,00 para saque</Text>
                    <br />
                    <Text type="secondary">
                      {painel.pode_sacar
                        ? 'Você pode solicitar o saque das comissões confirmadas.'
                        : 'Complete o valor mínimo para liberar o saque.'}
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          {painel.link_divulgacao && (
            <Card size="small" title="QR Code" style={{ marginTop: 16 }}>
              <Space direction="vertical" align="center">
                <div ref={qrRef}>
                  <QRCode value={painel.link_divulgacao} size={180} type="canvas" />
                </div>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    const canvas = qrRef.current?.querySelector('canvas');
                    if (canvas) {
                      const url = canvas.toDataURL('image/png');
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'qrcode-parceiro.png';
                      a.click();
                    }
                  }}
                >
                  Baixar imagem
                </Button>
              </Space>
            </Card>
          )}

          <Divider />

          <Card
            title={<><TeamOutlined /> Histórico de indicações</>}
            extra={
              <Link to="/tornar-se-parceiro">
                <Button type="link">Dados financeiros (PIX)</Button>
              </Link>
            }
          >
            <Table
              dataSource={indicacoes.results}
              columns={columns}
              rowKey="id"
              loading={loadingIndicacoes}
              pagination={{
                total: indicacoes.count,
                pageSize: 20,
                showSizeChanger: false,
                showTotal: (t) => `${t} indicação(ões)`,
              }}
              onChange={(pagination) => loadIndicacoes(pagination.current, pagination.pageSize)}
              locale={{ emptyText: 'Nenhuma indicação ainda.' }}
            />
          </Card>
        </>
      )}
    </div>
  );
}

export default PainelParceiroPage;
